import { EntityRefUtil, PdfExportRecordType, USERS_REGISTER_TITLE } from '@gearbelt/global';
import dayjs from 'dayjs';

// Single source of truth for PDF export download filenames, following the Gearbelt
// naming standard `{YYYY-MM-DD} - {Register} - {selection}.pdf` (e.g.
// `2026-08-05 - Inspections - 15 records.pdf`). Every scope derives its name here so
// the records / register / register-and-records downloads can't drift on date format,
// register name, or selection wording.

// Maps a recordType or CSV queryType slug to the Title-Case register name shown in the
// filename. Both singular (recordType, e.g. 'inspection') and plural (queryType, e.g.
// 'inspections') keys resolve so either dispatch path lands on the same register name.
const REGISTER_DISPLAY_NAMES: Record<string, string> = {
  inspection: 'Inspections',
  inspections: 'Inspections',
  repair: 'Repairs',
  repairs: 'Repairs',
  'schedule-form': 'Schedules',
  'schedule-forms': 'Schedules',
  issue: 'Issues',
  issues: 'Issues',
  'work-order': 'Work Orders',
  'work-orders': 'Work Orders',
  asset: 'Assets',
  assets: 'Assets',
  document: 'Documents',
  documents: 'Documents',
  location: 'Locations',
  locations: 'Locations',
  'location-log': 'Location Logs',
  'location-logs': 'Location Logs',
  'reading-log': 'Reading Logs',
  'reading-logs': 'Reading Logs',
  check: 'Schedule Checks',
  'schedule-check': 'Schedule Checks',
  'schedule-checks': 'Schedule Checks',
  // The 'users' queryType backs two registers, keyed by userType slug; titles
  // come from the shared map so the header and this filename can't disagree.
  ...USERS_REGISTER_TITLE,
};

export function registerDisplayName(slug: string): string {
  return REGISTER_DISPLAY_NAMES[slug] ?? slug;
}

export const REGISTER_SELECTION = 'register';

export function recordsSelection(recordCount: number): string {
  return `${recordCount} record${recordCount === 1 ? '' : 's'}`;
}

function recordRef(recordType: PdfExportRecordType, recordNumber: number): string {
  switch (recordType) {
    case 'inspection':
      return EntityRefUtil.createInspectionRef(recordNumber);
    case 'repair':
      return EntityRefUtil.createRepairRef(recordNumber);
    case 'schedule-form':
      return EntityRefUtil.createScheduleFormSubmissionRef(recordNumber);
    case 'issue':
      return EntityRefUtil.createIssueRef(recordNumber);
    case 'check':
      return EntityRefUtil.createCheckRef(recordNumber);
    case 'work-order':
      return EntityRefUtil.createWorkOrderRef(recordNumber);
  }
}

// A single-record download is named by the record's own reference (INS######,
// R######, SR######) rather than "1 record", matching the drawer and the CSV
// number column. Falls back to the count wording if the record has no number.
export function singleRecordSelection(recordType: PdfExportRecordType, record: { number?: number | null }): string {
  return record.number != null ? recordRef(recordType, record.number) : recordsSelection(1);
}

export function registerAndRecordsSelection(recordCount: number): string {
  return `register + ${recordsSelection(recordCount)}`;
}

export function buildPdfExportFilename(registerSlug: string, selection: string): string {
  return `${dayjs().format('YYYY-MM-DD')} - ${registerDisplayName(registerSlug)} - ${selection}.pdf`;
}
